import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, FormGroup, FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Product } from 'app/models/product.models';
import { ReceiptBook } from 'app/models/receiptbook.models';
import { SaleCondition } from 'app/models/condicionVenta.models';
import { Voucher } from 'app/models/voucher.models';
import { VoucherTypes } from 'app/models/vouchertype.models';
import { voucherTypesCode } from 'app/models/vouchertypescode.models';
import { VentasService } from 'app/services/ventas.service';
import { ImpresionService } from 'app/services/impresion.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-ventas',
  templateUrl: './ventas.component.html',
  styleUrls: ['./ventas.component.css']
})
export class VentasComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = ['comprobante', 'fecha', 'cliente', 'estadoPago', 'total', 'accion'];
  dataSource: MatTableDataSource<Voucher> = new MatTableDataSource();

  loading: boolean;
  ventas: Voucher[] = [];
  voucherTypes: VoucherTypes[] = [];
  voucherTypesCode: voucherTypesCode[] = [];
  saleConditions: SaleCondition[] = [];
  date = new UntypedFormControl();
  promedio = 0;
  totalVentas = 0;
  totalFacturas = 0;
  totalComprobantes = 0;

  // Filtros
  filtroForm: FormGroup;
  tiposComprobante = [
    { value: 'FACTURAS A', name: 'Factura A' },
    { value: 'FACTURAS B', name: 'Factura B' },
    { value: 'FACTURAS C', name: 'Factura C' },
    { value: 'NOTAS DE CREDITO A', name: 'Nota de Crédito A' },
    { value: 'NOTAS DE CREDITO B', name: 'Nota de Crédito B' },
    { value: 'NOTAS DE DEBITO A', name: 'Nota de Débito A' },
    { value: 'NOTAS DE DEBITO B', name: 'Nota de Débito B' }
  ];
  filtrosAplicados = false;
  receiptBooks: ReceiptBook[] = [];

  constructor(
    private ventasService: VentasService,
    public dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private router: Router,
    private fb: FormBuilder,
    private impresionService: ImpresionService
  ) { }

  ngOnInit(): void {
    const sidebar = <HTMLElement>document.querySelector('.navbar-brand');
    if (sidebar !== undefined) {
      sidebar.innerHTML = 'Ventas';
    }
    this.initFilterForm();
    this.getVentas();
  }

  initFilterForm() {
    // Obtener fechas para el rango por defecto (mes actual)
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    this.filtroForm = this.fb.group({
      fechaDesde: [firstDay],
      fechaHasta: [lastDay],
      tipoComprobante: [''],
      cliente: [''],
      enviado: [''],
      electronico: ['']
    });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  aplicarFiltros() {
    this.filtrosAplicados = true;
    this.loading = true;

    const filtros = {
      fechaDesde: this.filtroForm.value.fechaDesde ?
        this.formatDate(this.filtroForm.value.fechaDesde) : null,
      fechaHasta: this.filtroForm.value.fechaHasta ?
        this.formatDate(this.filtroForm.value.fechaHasta) : null,
      tipoComprobante: this.filtroForm.value.tipoComprobante,
      cliente: this.filtroForm.value.cliente,
      enviado: this.filtroForm.value.enviado,
      electronico: this.filtroForm.value.electronico
    };

    this.getVentasConFiltros(filtros);
  }

  limpiarFiltros() {
    this.filtrosAplicados = false;
    this.initFilterForm();
    this.getVentas();
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString();
    const day = date.getDate().toString();

    // Añadir ceros a la izquierda sin usar padStart
    const formattedMonth = month.length < 2 ? '0' + month : month;
    const formattedDay = day.length < 2 ? '0' + day : day;

    return `${year}-${formattedMonth}-${formattedDay}`;
  }

  getVentasConFiltros(filtros: any) {
    this.loading = true;
    this.dataSource.data = [];

    this.ventasService.getVentasConFiltros(filtros).subscribe((res) => {
      this.ventas = res.data;

      // Asignar los valores del dashboard correctamente
      if (res.dashboard) {
        this.promedio = res.dashboard.average;
        this.totalVentas = res.dashboard.totalValue;
        this.totalFacturas = res.dashboard.totalInvoices;
        this.totalComprobantes = res.dashboard.totalVouchers;
      } else {
        // Mantener la compatibilidad con el formato anterior si es necesario
        this.promedio = res.average || 0;
        this.totalVentas = res.totalValue || 0;
        this.totalFacturas = res.totalInvoices || 0;
        this.totalComprobantes = res.totalVouchers || 0;
      }

      this.dataSource.data = this.ventas;
      this.dataSource.paginator = this.paginator;
      this.loading = false;
    },
      err => {
        this.loading = false;
        this.openSnackBar('Error al obtener ventas filtradas', 'Cerrar');
      });
  }

  getVentas() {
    this.loading = true;
    this.dataSource.data = [];
    this.ventasService.getVentas().subscribe((res) => {
      this.ventas = res.data;
      this.voucherTypes = res.parameters.voucherTypes || [];
      this.voucherTypesCode = res.parameters.voucherTypesCode || [];
      this.receiptBooks = res.parameters.receiptBooks || [];
      this.saleConditions = res.parameters.saleConditions || [];
      
      // Asignar los valores del dashboard correctamente
      if (res.dashboard) {
        this.promedio = res.dashboard.average;
        this.totalVentas = res.dashboard.totalValue;
        this.totalFacturas = res.dashboard.totalInvoices;
        this.totalComprobantes = res.dashboard.totalVouchers;
      } else {
        // Mantener la compatibilidad con el formato anterior si es necesario
        this.promedio = res.average || 0;
        this.totalVentas = res.totalValue || 0;
        this.totalFacturas = res.totalInvoices || 0;
        this.totalComprobantes = res.totalVouchers || 0;
      }
      this.dataSource.data = this.ventas;
      this.dataSource.paginator = this.paginator;
      this.loading = false;
    },
      err => {
        this.loading = false;
        this.openSnackBar('Error al cargar las ventas', 'Cerrar');
      })
  }

  openDialog(producto?: Product) {
    this.router.navigate(['/productos', 'create']);
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 2000,
    });
  }

  eliminar(voucher: Voucher) {
    Swal.fire({
      title: `¿Desea eliminar el comprobante seleccionado?`,
      text: `Eliminar Comprobante`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si, eliminalo!',
      cancelButtonText: 'No'
    }).then((result) => {
      if (result.isConfirmed) {
        this.ventasService.eliminarVenta(voucher.id).subscribe(
          (data) => {
            this.ventas = data;
            this.getVentas();
            this.openSnackBar(`Comprobante eliminado correctamente`, 'Información');
          },
          (err) => {
            this.openSnackBar(`Error al eliminar el comprobante: ${err.message || 'Error desconocido'}`, 'Cerrar');
          }
        );
      }
    })
  }

  // Métodos para la nueva tabla mejorada
  getTipoClass(tipoComprobante: string): string {
    // Aplicar el estilo de factura a todos los comprobantes
    return 'tipo-factura';
  }

  getEstadoAfipClass(row: any): string {
    if (row?.electronic) {
      if (row?.sent) {
        return 'afip-aceptado';
      } else {
        return 'afip-pendiente';
      }
    }
    return 'afip-na';
  }

  getEstadoAfipText(row: any): string {
    if (row?.electronic) {
      if (row?.sent) {
        return 'ACEPTADO';
      } else {
        return 'PENDIENTE';
      }
    }
    return 'N/A';
  }

  getEstadoPagoClass(row: any): string {
    // Validar que el objeto row existe
    if (!row) return 'pago-na';

    // Si el monto total es 0, no hay estado de pago aplicable
    if (row.totalAmount === 0) return 'pago-na';

    // Para facturas de crédito, el estado de pago no es aplicable
    if (row.header?.receiptBook?.description?.toLowerCase().includes('credito')) {
      return 'pago-na';
    }

    // Usar el campo paymentStatus si está disponible
    if (row.paymentStatus) {
      switch (row.paymentStatus.toLowerCase()) {
        case 'pagado':
        case 'paid':
        case 'completo':
        case 'complete':
          return 'pago-pagado';
        case 'pendiente':
        case 'pending':
        case 'parcial':
        case 'partial':
          return 'pago-pendiente';
        case 'vencido':
        case 'overdue':
          return 'pago-vencido';
        case 'cancelado':
        case 'cancelled':
          return 'pago-cancelado';
        default:
          return 'pago-pendiente';
      }
    }

    // Fallback: Estado basado en la condición de venta usando el nuevo modelo SaleCondition
    const saleConditionId = row.header?.saleConditionId;
    
    if (saleConditionId && this.saleConditions.length > 0) {
      const saleCondition = this.saleConditions.find(sc => sc.id === saleConditionId);
      
      if (saleCondition) {
        // Si afecta cuenta corriente (1), es pendiente
        if (saleCondition.affectsCurrentAccount === 1) {
          return 'pago-pendiente';
        } else {
          // Si no afecta cuenta corriente (0), es contado/pagado
          return 'pago-pagado';
        }
      }
    }

    // Fallback al método anterior para compatibilidad
    const saleCondition = row.header?.saleCondition;
    if (saleCondition === 'contado') {
      return 'pago-pagado';
    } else if (saleCondition === 'ctacte') {
      return 'pago-pendiente';
    }

    // Si no hay condición de venta definida, retornamos pendiente por defecto
    return 'pago-pendiente';
  }

  getEstadoPagoText(row: any): string {
    const estado = this.getEstadoPagoClass(row);

    switch (estado) {
      case 'pago-pagado': return 'PAGADO';
      case 'pago-pendiente': return 'PENDIENTE';
      case 'pago-vencido': return 'VENCIDO';
      case 'pago-cancelado': return 'CANCELADO';
      case 'pago-na': return 'N/A';
      default: return 'PENDIENTE';
    }
  }

  getTotalClass(total: number): string {
    if (total > 0) return 'total-positivo';
    if (total < 0) return 'total-negativo';
    return 'total-neutro';
  }

  /**
   * Obtiene la información de la condición de venta para un voucher
   * @param row Fila del voucher
   * @returns Objeto con información de la condición de venta
   */
  getSaleConditionInfo(row: any): { condition: SaleCondition | null, name: string, affectsCurrentAccount: boolean } {
    const saleConditionId = row.header?.saleConditionId;
    
    if (saleConditionId && this.saleConditions.length > 0) {
      const condition = this.saleConditions.find(sc => sc.id === saleConditionId && sc.isActive === 1);
      
      if (condition) {
        return {
          condition,
          name: condition.name,
          affectsCurrentAccount: condition.affectsCurrentAccount === 1
        };
      }
    }

    // Fallback para compatibilidad con el formato anterior
    const legacyCondition = row.header?.saleCondition;
    return {
      condition: null,
      name: legacyCondition || 'Sin definir',
      affectsCurrentAccount: legacyCondition === 'ctacte'
    };
  }

  /**
   * Obtiene el texto descriptivo de la condición de venta
   * @param row Fila del voucher
   * @returns Texto descriptivo de la condición
   */
  getSaleConditionText(row: any): string {
    const info = this.getSaleConditionInfo(row);
    
    if (info.condition) {
      let text = info.condition.name;
      if (info.condition.dueDays > 0) {
        text += ` (${info.condition.dueDays} días)`;
      }
      return text;
    }
    
    return info.name;
  }

  getRowClass(row: any): string {
    const classes = [];

    // Clase basada en el tipo
    const tipoClase = this.getTipoClass(row?.header?.receiptBook?.description);
    if (tipoClase) {
      classes.push(`row-${tipoClase.replace('tipo-', '')}`);
    }

    // Clase basada en el estado de pago
    const estadoPago = this.getEstadoPagoClass(row);
    if (estadoPago === 'pago-pagado') {
      classes.push('row-pagado');
    } else if (estadoPago === 'pago-pendiente') {
      classes.push('row-pendiente');
    }

    return classes.join(' ');
  }

  /**
   * Valida si un tipo de comprobante es válido para operaciones de venta
   * @param voucherTypeName Nombre del tipo de comprobante
   * @returns true si es válido para ventas, false si no
   */
  validateVoucherTypeForSales(voucherTypeName: string): boolean {
    if (!voucherTypeName) {
      this.showValidationError('Tipo de comprobante no encontrado');
      return false;
    }

    // Buscar el voucherType correspondiente
    const voucherType = this.voucherTypes.find(vt =>
      vt.description?.toLowerCase().includes(voucherTypeName.toLowerCase())
    );

    if (!voucherType) {
      this.showValidationError('Tipo de comprobante no encontrado en el sistema');
      return false;
    }

    // Buscar el voucherTypesCode correspondiente usando el ID
    const voucherTypeCode = this.voucherTypesCode.find(vtc =>
      vtc.id === voucherType.voucherTypesCodeId
    );

    if (!voucherTypeCode) {
      this.showValidationError('Configuración de tipo de comprobante no encontrada');
      return false;
    }

    // Validación principal: debe permitir ventas
    if (!voucherTypeCode.isSale) {
      this.showValidationError(
        `El tipo de comprobante "${voucherTypeName}" no está configurado para operaciones de venta`
      );
      return false;
    }

    // Validación adicional: si es solo de compras, mostrar advertencia pero permitir
    // (ya que en los datos reales vemos que un tipo puede ser tanto de compra como de venta)
    if (voucherTypeCode.isPurchase && voucherTypeCode.isSale) {
      // Tipo configurado para compras y ventas - esto es normal
    }

    // Validación del operationType en el voucherType como respaldo
    if (voucherType.operationType && !voucherType.operationType.includes('Sale')) {
      // OperationType podría no ser apropiado para ventas - log removido
    }

    return true;
  }

  /**
   * Valida si un tipo de código de comprobante es válido para operaciones de venta
   * comparando directamente por el nombre del voucherTypesCode
   * @param voucherTypesCodeName Nombre del código de tipo de comprobante
   * @returns true si es válido para ventas, false si no
   */
  validateVoucherTypesCodeForSales(voucherTypesCodeName: string): boolean {
    if (!voucherTypesCodeName) {
      this.showValidationError('Nombre del código de tipo de comprobante no encontrado');
      return false;
    }

    // Buscar directamente en voucherTypesCode por nombre
    const voucherTypeCode = this.voucherTypesCode.find(vtc =>
      vtc.name?.toLowerCase() === voucherTypesCodeName.toLowerCase()
    );

    if (!voucherTypeCode) {
      this.showValidationError(`Código de tipo de comprobante "${voucherTypesCodeName}" no encontrado`);
      return false;
    }

    // Validar si permite ventas
    if (!voucherTypeCode.isSale) {
      this.showValidationError(
        `El código "${voucherTypesCodeName}" no está configurado para operaciones de venta`
      );
      return false;
    }

    // Validación adicional: verificar que no sea solo de compras
    if (voucherTypeCode.isPurchase && !voucherTypeCode.isSale) {
      this.showValidationError(
        `El código "${voucherTypesCodeName}" está configurado solo para compras`
      );
      return false;
    }

    return true;
  }

  /**
   * Validación específica para comprobantes de venta usando datos reales del backend
   * @param voucherTypeId ID del tipo de comprobante a validar (opcional)
   * @param voucherTypesCodeName Nombre del código de comprobante (opcional)
   * @returns true si es válido para ventas, false si no
   */
  validateForSalesOperation(voucherTypeId?: number, voucherTypesCodeName?: string): boolean {
    // Si se proporciona voucherTypeId, validar por ID
    if (voucherTypeId) {
      const voucherType = this.voucherTypes.find(vt => vt.id === voucherTypeId);
      if (!voucherType) return false;

      const voucherTypeCode = this.voucherTypesCode.find(vtc => vtc.id === voucherType.voucherTypesCodeId);
      if (!voucherTypeCode) return false;

      return !!voucherTypeCode.isSale;
    }

    // Si se proporciona el nombre del código, validar por nombre
    if (voucherTypesCodeName) {
      return this.validateVoucherTypesCodeForSales(voucherTypesCodeName);
    }

    return false;
  }

  /**
   * Obtiene todos los tipos de comprobante válidos para ventas
   * @returns Array de VoucherTypes válidos para ventas
   */
  getValidSalesVoucherTypes(): VoucherTypes[] {
    return this.voucherTypes.filter(vt => {
      const vtCode = this.voucherTypesCode.find(vtc => vtc.id === vt.voucherTypesCodeId);
      return vtCode && !!vtCode.isSale;
    });
  }

  /**
   * Obtiene todas las condiciones de venta activas
   * @returns Array de condiciones de venta activas
   */
  getActiveSaleConditions(): SaleCondition[] {
    return this.saleConditions.filter(sc => sc.isActive === 1);
  }

  /**
   * Valida si una condición de venta es válida para usar
   * @param saleConditionId ID de la condición de venta
   * @returns true si es válida, false si no
   */
  validateSaleCondition(saleConditionId: number): boolean {
    if (!saleConditionId) {
      this.showValidationError('ID de condición de venta no proporcionado');
      return false;
    }

    const condition = this.saleConditions.find(sc => sc.id === saleConditionId);
    
    if (!condition) {
      this.showValidationError('Condición de venta no encontrada');
      return false;
    }

    if (condition.isActive === 0) {
      this.showValidationError(`La condición de venta "${condition.name}" está inactiva`);
      return false;
    }

    if (condition.deletedAt) {
      this.showValidationError(`La condición de venta "${condition.name}" ha sido eliminada`);
      return false;
    }

    return true;
  }

  /**
   * Obtiene condiciones de venta que afectan cuenta corriente
   * @returns Array de condiciones que afectan cuenta corriente
   */
  getCurrentAccountSaleConditions(): SaleCondition[] {
    return this.saleConditions.filter(sc => 
      sc.isActive === 1 && 
      sc.affectsCurrentAccount === 1 && 
      !sc.deletedAt
    );
  }

  /**
   * Obtiene condiciones de venta de contado (no afectan cuenta corriente)
   * @returns Array de condiciones de contado
   */
  getCashSaleConditions(): SaleCondition[] {
    return this.saleConditions.filter(sc => 
      sc.isActive === 1 && 
      sc.affectsCurrentAccount === 0 && 
      !sc.deletedAt
    );
  }

  /**
   * Busca un talonario válido para un tipo de comprobante específico
   * SIEMPRE usa la ruta: parameters.receiptBooks[].voucherType.voucherTypesCode.name
   * @param voucherTypesCodeName Nombre exacto del tipo de comprobante (ej: 'Factura', 'Remito', 'Nota de Crédito')
   * @returns ReceiptBook encontrado o undefined
   */
  private findReceiptBookByVoucherTypesCodeName(voucherTypesCodeName: string): ReceiptBook | undefined {
    const foundReceiptBook = this.receiptBooks.find(rb => {
      // RUTA OBLIGATORIA: receiptBooks[].voucherType.voucherTypesCode.name
      const currentVoucherTypesCodeName = rb.voucherType?.voucherTypesCode?.name;
      const isForSales = rb.receiptBookType?.value === 'sales' || rb.operation === 'venta';
      const isActive = !rb.deletedAt && (rb.status === undefined || rb.status === true);
      const hasValidVoucherType = !!rb.voucherType?.voucherTypesCode;
      
      return hasValidVoucherType && 
             currentVoucherTypesCodeName === voucherTypesCodeName && 
             isForSales && 
             isActive;
    });

    return foundReceiptBook;
  }

  /**
   * Valida antes de navegar a crear una factura
   */
  validateAndNavigateToFactura(): void {
    // Verificar que los datos estén cargados
    if (!this.receiptBooks.length) {
      this.showValidationError('Los talonarios aún no están cargados.');
      return;
    }

    // Verificar que las condiciones de venta estén cargadas
    if (!this.saleConditions.length) {
      this.showValidationError('Las condiciones de venta aún no están cargadas.');
      return;
    }

    // Verificar que haya al menos una condición de venta activa
    const activeSaleConditions = this.getActiveSaleConditions();
    if (activeSaleConditions.length === 0) {
      this.showValidationError('No hay condiciones de venta activas disponibles.');
      return;
    }

    // Buscar talonario usando la ruta estándar: receiptBooks[].voucherType.voucherTypesCode.name
    const facturaReceiptBook = this.findReceiptBookByVoucherTypesCodeName('Factura');

    if (!facturaReceiptBook) {
      this.showValidationError('No se encontró un talonario válido para "Factura" o no está configurado para ventas.');
      return;
    }

    this.router.navigate(['/comprobante/ventas/create/factura']);
  }

  /**
   * Valida antes de navegar a crear cualquier tipo de comprobante desde el menú
   * SIEMPRE usa la ruta: parameters.receiptBooks[].voucherType.voucherTypesCode.name
   * @param comprobanteType Tipo de comprobante a crear
   */
  validateAndNavigateToComprobante(comprobanteType: string): void {
    // Si aún está cargando, mostrar mensaje específico
    if (this.loading) {
      this.showValidationError('Los datos aún se están cargando. Por favor espere...');
      return;
    }
    
    // Verificar que los datos esenciales estén cargados
    if (!this.receiptBooks || !this.receiptBooks.length) {
      this.showValidationError('Los talonarios aún no están cargados. Intente recargar la página.');
      return;
    }

    // Verificar que los receiptBooks tengan la estructura correcta
    const validReceiptBooks = this.receiptBooks.filter(rb => 
      rb.voucherType && 
      rb.voucherType.voucherTypesCode && 
      rb.voucherType.voucherTypesCode.name
    );
    
    if (validReceiptBooks.length === 0) {
      this.showValidationError('Los talonarios no tienen la información completa de tipos de comprobante.');
      return;
    }

    // Verificar que las condiciones de venta estén cargadas
    if (!this.saleConditions.length) {
      this.showValidationError('Las condiciones de venta aún no están cargadas. Intente nuevamente.');
      return;
    }

    // Verificar que haya condiciones de venta activas
    const activeSaleConditions = this.getActiveSaleConditions();
    if (activeSaleConditions.length === 0) {
      this.showValidationError('No hay condiciones de venta activas disponibles para crear comprobantes.');
      return;
    }

    // Mapear tipos de comprobante del router a nombres EXACTOS de voucherTypesCode.name
    const typeMapping: { [key: string]: string[] } = {
      'factura': ['Factura'],
      'pedido': ['Pedido'],
      'presupuesto': ['Presupuesto'],
      'remito': ['Remito'],
      'notadecredito': ['Nota de Crédito', 'Nota de Credito'],
      'notadedebito': ['Nota de Débito', 'Nota de Debito']
    };

    const possibleNames = typeMapping[comprobanteType] || [comprobanteType];

    // Buscar talonario usando la función helper que garantiza el uso de la ruta correcta
    let foundReceiptBook: ReceiptBook | undefined;
    
    for (const possibleName of possibleNames) {
      foundReceiptBook = this.findReceiptBookByVoucherTypesCodeName(possibleName);
      if (foundReceiptBook) {
        break;
      }
    }

    if (!foundReceiptBook) {
      const buttonName = this.getButtonDisplayName(comprobanteType);
      this.showValidationError(`No se encontró un talonario activo para "${buttonName}". Verifique la configuración de talonarios.`);
      return;
    }

    // Validar que el voucherTypesCode permite ventas (doble verificación)
    const voucherTypesCode = foundReceiptBook.voucherType?.voucherTypesCode;
    if (voucherTypesCode && !voucherTypesCode.isSale) {
      const buttonName = this.getButtonDisplayName(comprobanteType);
      this.showValidationError(`El tipo de comprobante "${buttonName}" no está configurado para operaciones de venta.`);
      return;
    }
    
    // Si pasa todas las validaciones, navegar
    this.router.navigate([`/comprobante/ventas/create/${comprobanteType}`]);
  }

  /**
   * Obtiene todos los tipos de comprobante disponibles basados en voucherTypesCode.name
   * SIEMPRE usa la ruta: parameters.receiptBooks[].voucherType.voucherTypesCode.name
   * @returns Array con los tipos de comprobante únicos disponibles para ventas
   */
  getAvailableVoucherTypes(): string[] {
    const availableTypes: string[] = [];
    
    this.receiptBooks.forEach(rb => {
      // Usar SIEMPRE la ruta: receiptBooks[].voucherType.voucherTypesCode.name
      const voucherTypesCodeName = rb.voucherType?.voucherTypesCode?.name;
      const isForSales = rb.receiptBookType?.value === 'sales' || rb.operation === 'venta';
      const isActive = !rb.deletedAt && (rb.status === undefined || rb.status === true);
      const isSaleAllowed = rb.voucherType?.voucherTypesCode?.isSale;
      
      if (voucherTypesCodeName && isForSales && isActive && isSaleAllowed && !availableTypes.includes(voucherTypesCodeName)) {
        availableTypes.push(voucherTypesCodeName);
      }
    });
    
    return availableTypes;
  }

  /**
   * Verifica si un tipo de comprobante específico está disponible para ventas
   * SIEMPRE usa la ruta: parameters.receiptBooks[].voucherType.voucherTypesCode.name
   * @param voucherTypesCodeName Nombre del tipo de comprobante a verificar
   * @returns true si está disponible, false si no
   */
  isVoucherTypeAvailable(voucherTypesCodeName: string): boolean {
    return !!this.findReceiptBookByVoucherTypesCodeName(voucherTypesCodeName);
  }

  /**
   * Obtiene información detallada de todos los talonarios para debugging
   * SIEMPRE usa la ruta: parameters.receiptBooks[].voucherType.voucherTypesCode.name
   * @returns Array con información detallada de todos los talonarios
   */
  getReceiptBooksDebugInfo(): any[] {
    return this.receiptBooks.map(rb => ({
      id: rb.id,
      description: rb.description,
      // RUTA OBLIGATORIA: receiptBooks[].voucherType.voucherTypesCode.name
      voucherTypesCodeName: rb.voucherType?.voucherTypesCode?.name,
      voucherTypesCodeId: rb.voucherType?.voucherTypesCode?.id,
      isSale: rb.voucherType?.voucherTypesCode?.isSale,
      isPurchase: rb.voucherType?.voucherTypesCode?.isPurchase,
      receiptBookTypeValue: rb.receiptBookType?.value,
      operation: rb.operation,
      isActive: !rb.deletedAt && (rb.status === undefined || rb.status === true),
      deletedAt: rb.deletedAt,
      status: rb.status,
      voucherTypeId: rb.voucherTypeId,
      receiptBookTypeId: rb.receiptBookTypeId
    }));
  }
  private showValidationError(message: string): void {
    Swal.fire({
      icon: 'error',
      title: 'Validación de Comprobante',
      text: message,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#3085d6'
    });
  }

  /**
   * Obtiene el nombre legible del botón basado en el tipo de comprobante
   * @param comprobanteType Tipo de comprobante del router
   * @returns Nombre legible para mostrar al usuario
   */
  private getButtonDisplayName(comprobanteType: string): string {
    const displayNames: { [key: string]: string } = {
      'factura': 'Nueva Factura',
      'pedido': 'Nuevo Pedido',
      'presupuesto': 'Nuevo Presupuesto',
      'remito': 'Nuevo Remito',
      'notadecredito': 'Nueva Nota de Crédito',
      'notadedebito': 'Nueva Nota de Débito'
    };

    return displayNames[comprobanteType] || comprobanteType;
  }

  /**
   * Navega al detalle del cliente
   * @param clientId ID del cliente
   */
  navigateToClient(clientId: string): void {
    if (clientId) {
      this.router.navigate(['/clientes/resumen', clientId]);
    } else {
      this.openSnackBar('ID de cliente no válido', 'Cerrar');
    }
  }

  /**
   * Imprime un comprobante de venta
   * @param voucher Comprobante a imprimir
   */
  imprimirComprobante(voucher: Voucher): void {
    if (!voucher || !voucher.id) {
      this.openSnackBar('Error: Comprobante no válido para imprimir', 'Cerrar');
      return;
    }

    // Usar el servicio de impresión centralizado
    this.impresionService.imprimirComprobante(voucher.id, 'ventas');
  }

}
